import { InputElement } from '@chakra-ui/react';
import {
  IQuoteItemRespSchema,
  IQuoteOfferingRespSchema,
  IQuotePrice,
  IQuoteRespSchema,
  IRateResSchema,
  PriceUpliftConfigFormType,
  PriceUpliftConfigTypeEnum,
  PriceUpliftPercentageTypeEnum,
  QuoteOfferingForUplift,
} from '@monetize/types/app';
import React from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import {
  MBox,
  MCheckbox,
  MCustomNumberInput,
  MGrid,
  MGridItem,
  MText,
  MTooltip,
} from '../Monetize';
import { PriceUpliftPpu } from './PriceUpliftPpu';

export type PriceUpliftGridProps = {
  namePrefix: `segments.${number}` | 'current'; // prefix for RHF field names
  showUplift?: boolean;
  ppuLabel?: string;
  isLockedOrIsReadOnly: boolean;
  items: IQuoteItemRespSchema[];
  control: Control<PriceUpliftConfigFormType>;
  percentageType: PriceUpliftPercentageTypeEnum;
  quote: IQuoteRespSchema;
  quoteOffering: IQuoteOfferingRespSchema | QuoteOfferingForUplift;
  quotePrices: IQuotePrice[];
  offeringRate: IRateResSchema;
  parentQuoteOfferingId?: string;
  setValue: UseFormSetValue<PriceUpliftConfigFormType>;
  calculateUpliftForAllItems?: (
    upliftPercentage: string,
    quoteOffering: QuoteOfferingForUplift,
  ) => void;
  calculateUpliftForSingleItem?: (
    upliftPercentage: string,
    quoteOffering: QuoteOfferingForUplift,
    productId: string,
  ) => void;
  clearProductUpliftsForSegment?: (
    quoteOffering: QuoteOfferingForUplift,
  ) => void;
};

export const PriceUpliftGrid: React.FC<PriceUpliftGridProps> = ({
  namePrefix,
  showUplift,
  ppuLabel = 'Renewal PPU',
  isLockedOrIsReadOnly,
  items,
  control,
  percentageType,
  quote,
  quoteOffering,
  quotePrices,
  offeringRate,
  parentQuoteOfferingId,
  setValue,
  calculateUpliftForAllItems,
  calculateUpliftForSingleItem,
  clearProductUpliftsForSegment,
}) => {
  const type = useWatch({ control, name: `${namePrefix}.type` as any });
  const isApplyToAll = type === PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS;
  const globalUplift =
    useWatch({ control, name: `${namePrefix}.amount` as any }) ?? 0;

  const gridColumns = showUplift ? '3fr 1fr 2fr' : '3fr 2fr';

  return (
    <MBox w="full">
      <MGrid templateColumns={gridColumns} alignItems="center" py={2}>
        <MGridItem />
        {showUplift && (
          <MGridItem>
            <MText fontWeight="semibold">Uplift</MText>
          </MGridItem>
        )}
        <MGridItem>
          <MText fontWeight="semibold" textAlign="right">
            {ppuLabel}
          </MText>
        </MGridItem>
      </MGrid>

      {showUplift && items.length > 1 && (
        <MGrid templateColumns={gridColumns} alignItems="center" py={2}>
          <MGridItem>
            <Controller
              control={control}
              name={`${namePrefix}.type` as any}
              render={({ field: { value } }) => (
                <MCheckbox
                  isReadOnly={isLockedOrIsReadOnly}
                  isChecked={
                    value === PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS
                  }
                  onChange={(ev) => {
                    const newValue = ev.target.checked
                      ? PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS
                      : PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC;

                    if (!ev.target.checked) {
                      setValue(`${namePrefix}.type` as any, newValue, {
                        shouldDirty: false,
                      });
                      setValue(`${namePrefix}.amount` as any, '', {
                        shouldDirty: false,
                      });
                      items.forEach((_, idx) => {
                        setValue(
                          `${namePrefix}.productConfigurations.${idx}.amount` as any,
                          '',
                          { shouldDirty: false },
                        );
                      });

                      if (calculateUpliftForAllItems && quoteOffering) {
                        calculateUpliftForAllItems('', quoteOffering);
                      }
                    } else {
                      setValue(`${namePrefix}.type` as any, newValue, {
                        shouldDirty: false,
                      });

                      if (clearProductUpliftsForSegment && quoteOffering) {
                        clearProductUpliftsForSegment(
                          quoteOffering as QuoteOfferingForUplift,
                        );
                      }
                    }
                  }}
                >
                  Apply to all
                </MCheckbox>
              )}
            />
          </MGridItem>
          <MGridItem>
            <Controller
              control={control}
              name={`${namePrefix}.amount` as any}
              render={({ field: { value, onChange, ...rest } }) => (
                <MBox position="relative">
                  <MCustomNumberInput
                    isReadOnly={isLockedOrIsReadOnly}
                    isDisabled={!isApplyToAll}
                    inputMode="decimal"
                    value={value ?? ''}
                    onChange={(newValue) => {
                      onChange(newValue);
                      if (
                        calculateUpliftForAllItems &&
                        isApplyToAll &&
                        quoteOffering
                      ) {
                        calculateUpliftForAllItems(newValue, quoteOffering);
                      }
                    }}
                    placeholder="0"
                    {...rest}
                  />
                  {/* v2's InputGroup sized this as a 30px square at the input's
                      edge; v3's InputElement only gets a `top` inside a group. */}
                  <InputElement
                    placement="end"
                    top="0"
                    w="30px"
                    px="0"
                    fontWeight="bold"
                  >
                    %
                  </InputElement>
                </MBox>
              )}
            />
          </MGridItem>
          <MGridItem />
        </MGrid>
      )}

      {items.map((item, idx) => (
        <MGrid
          key={item.id}
          templateColumns={gridColumns}
          alignItems="center"
          py={2}
        >
          <MGridItem>
            <MTooltip label={item.productName}>
              <MText lineClamp={1}>{item.productName}</MText>
            </MTooltip>
          </MGridItem>
          {showUplift && (
            <MGridItem>
              <Controller
                control={control}
                name={
                  `${namePrefix}.productConfigurations.${idx}.amount` as any
                }
                render={({ field: { value, onChange, ...rest } }) => (
                  <MBox position="relative">
                    <MCustomNumberInput
                      isReadOnly={isLockedOrIsReadOnly}
                      isDisabled={isApplyToAll}
                      inputMode="decimal"
                      value={isApplyToAll ? globalUplift : (value ?? '')}
                      onChange={(upliftAmount) => {
                        onChange(upliftAmount);
                        setValue(
                          `${namePrefix}.productConfigurations.${idx}.productId` as any,
                          item.productId,
                        );
                        setValue(
                          `${namePrefix}.productConfigurations.${idx}.percentageType` as any,
                          percentageType,
                        );

                        if (
                          calculateUpliftForSingleItem &&
                          quoteOffering &&
                          !isApplyToAll
                        ) {
                          calculateUpliftForSingleItem(
                            upliftAmount,
                            quoteOffering as QuoteOfferingForUplift,
                            item.productId,
                          );
                        }
                      }}
                      placeholder="0"
                      {...rest}
                    />
                    <InputElement
                      placement="end"
                      top="0"
                      w="30px"
                      px="0"
                      fontWeight="bold"
                    >
                      %
                    </InputElement>
                  </MBox>
                )}
              />
            </MGridItem>
          )}
          <MGridItem textAlign="right">
            <PriceUpliftPpu
              item={item}
              quote={quote}
              quotePrices={quotePrices}
              offeringRate={offeringRate}
              productType={item.productType}
              parentQuoteOfferingId={parentQuoteOfferingId}
              rateId={quoteOffering?.rateId}
            />
          </MGridItem>
        </MGrid>
      ))}
    </MBox>
  );
};
